/**
 * "You can also Try" carousel on the subscription product page.
 *
 * Each card adds its own product straight to the cart, with or without a
 * selling plan, and hands the response to the cart drawer the same way the
 * product form does.
 */

class SubscriptionCrossSell extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.track = this.querySelector("[data-cross-sell-track]");
    this.errorMessage = this.querySelector("[data-cross-sell-error]");
    this.items = Array.from(this.querySelectorAll(".cross-sell__item"));

    if (!this.track || !this.items.length) return;

    this.initialized = true;

    this.addEventListener("change", (event) => this.handlePlanChange(event));
    this.addEventListener("click", (event) => this.handleClick(event));

    this.buildDots();
    this.bindDrag();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.dragListeners?.abort();
    this.endDrag();
  }

  get cart() {
    return (
      document.querySelector("cart-notification") || document.querySelector("cart-drawer")
    );
  }

  static formatMoney(cents) {
    const value = Number(cents) / 100;

    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }

  static selectedOption(picker) {
    return picker?.querySelector('[role="option"][aria-selected="true"]');
  }

  /**
   * Both prices come off the option that was picked rather than off the card,
   * since each plan discounts the variant differently. A one-time purchase
   * costs the full price, which leaves nothing to strike through.
   */
  handlePlanChange(event) {
    const picker = event.target.closest?.("plan-select");
    const item = picker?.closest(".cross-sell__item");
    if (!item) return;

    const option = SubscriptionCrossSell.selectedOption(picker);
    if (!option) return;

    const price = Number(option.dataset.price);
    const fullPrice = Number(option.dataset.fullPrice);
    const current = item.querySelector("[data-cross-sell-price]");
    const compare = item.querySelector("[data-compare-price]");
    const was = item.querySelector("[data-compare-wrapper]");

    if (current) current.textContent = SubscriptionCrossSell.formatMoney(price);
    if (compare) compare.textContent = SubscriptionCrossSell.formatMoney(fullPrice);
    if (was) was.hidden = price >= fullPrice;

    const button = item.querySelector(".cross-sell__add");
    const available = option.dataset.available === "true";

    if (button) {
      button.disabled = !available;
      button.textContent = available
        ? button.dataset.addLabel
        : button.dataset.soldOutLabel;
    }
  }

  handleClick(event) {
    const dot = event.target.closest(".cross-sell__dot");
    if (dot) {
      const item = this.items[Number(dot.dataset.index)];

      if (item) this.scrollToItem(item);

      return;
    }

    const button = event.target.closest(".cross-sell__add");
    if (button && !button.disabled) this.addToCart(button);
  }

  async addToCart(button) {
    const item = button.closest(".cross-sell__item");
    const picker = item?.querySelector("plan-select");
    const option = SubscriptionCrossSell.selectedOption(picker);
    const cart = this.cart;

    // The option carries the variant to add alongside its plan. The button's
    // own id is what stands in without script.
    const line = {
      id: Number(option?.dataset.value || button.dataset.variantId),
      quantity: 1,
    };

    if (option?.dataset.plan) line.selling_plan = Number(option.dataset.plan);

    const body = { items: [line] };

    // Asking for the cart sections in the same request is what lets the drawer
    // be redrawn without a second round trip.
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    this.errorMessage?.setAttribute("hidden", "");
    button.classList.add("loading");
    button.disabled = true;

    try {
      const response = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      // Shopify answers an unaddable line with 422 and a `status` field rather
      // than a network error, so a rejected add has to be read off the body.
      if (data.status) {
        this.showError(data.description);
        return;
      }

      if (cart) cart.renderContents(data);
      else window.location = window.routes?.cart_url || "/cart";
    } catch (error) {
      this.showError();
    } finally {
      button.classList.remove("loading");
      button.disabled = false;
    }
  }

  /** Where the track has to be scrolled to for the item to sit at its start. */
  itemOffset(item) {
    return (
      this.track.scrollLeft +
      item.getBoundingClientRect().left -
      this.track.getBoundingClientRect().left
    );
  }

  /**
   * scrollIntoView walks up every scrollable ancestor, so moving sideways
   * inside the track takes the whole page with it. Scrolling the track on its
   * own keeps the movement where it belongs.
   */
  scrollToItem(item) {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.track.scrollTo({
      left: this.itemOffset(item),
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }

  nearestIndex(scrollLeft) {
    let nearest = 0;
    let distance = Infinity;

    this.items.forEach((item, index) => {
      const gap = Math.abs(this.itemOffset(item) - scrollLeft);
      if (gap < distance) {
        distance = gap;
        nearest = index;
      }
    });

    return nearest;
  }

  /**
   * Touch and trackpads already scroll the track natively; a mouse cannot, so
   * dragging with one is added here. Pen and touch are left to the browser.
   * The dots stay the single-pointer alternative WCAG 2.5.7 asks for.
   */
  bindDrag() {
    if (this.items.length < 2) return;

    this.dragListeners = new AbortController();
    const { signal } = this.dragListeners;

    this.track.dataset.draggable = "";
    this.track.addEventListener("pointerdown", (event) => this.startDrag(event), { signal });

    // Images and links would otherwise be picked up by the browser's own
    // drag-and-drop and leave a ghost under the cursor.
    this.track.addEventListener("dragstart", (event) => event.preventDefault(), { signal });

    // A drag that ends over a link or the image would still count as a click
    // on it, so the click that closes a drag is swallowed before it lands.
    this.track.addEventListener(
      "click",
      (event) => {
        if (!this.suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      },
      { capture: true, signal }
    );
  }

  startDrag(event) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;

    // The controls keep their own clicks; the picker in particular opens on
    // pointerdown and would be dragged out from under its own list.
    if (event.target.closest("button, plan-select, input, select, textarea, label")) return;
    if (this.track.scrollWidth <= this.track.clientWidth) return;

    this.endDrag();

    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScroll: this.track.scrollLeft,
      startIndex: this.nearestIndex(this.track.scrollLeft),
      moved: false,
      listeners: new AbortController(),
    };

    // Listened for on the window so a release outside the track still ends it.
    const { signal } = this.drag.listeners;
    window.addEventListener("pointermove", (moveEvent) => this.moveDrag(moveEvent), { signal });
    window.addEventListener("pointerup", (upEvent) => this.releaseDrag(upEvent), { signal });
    window.addEventListener("pointercancel", (upEvent) => this.releaseDrag(upEvent), { signal });
  }

  moveDrag(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const delta = event.clientX - drag.startX;

    // A few pixels of wobble is still a click, not a drag.
    if (!drag.moved) {
      if (Math.abs(delta) < 6) return;

      drag.moved = true;
      window.getSelection()?.removeAllRanges();

      // Mandatory snapping would pull the track back to a card on every frame.
      // A restore still pending from the last drag would switch it back on.
      this.cancelSnapRestore();
      this.track.style.scrollSnapType = "none";
      this.track.classList.add("cross-sell__track--dragging");
    }

    event.preventDefault();
    this.track.scrollLeft = drag.startScroll - delta;
  }

  releaseDrag(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this.endDrag();
    if (!drag.moved) return;

    this.suppressClick = true;
    // pointerup, mouseup and click share a task, so this only clears the flag
    // when the release landed somewhere that never produced a click.
    setTimeout(() => {
      this.suppressClick = false;
    });

    // A short, deliberate flick should still move a whole card, so past a
    // fifth of the track the drag direction decides rather than the distance.
    const delta = event.clientX - drag.startX;
    let target = this.nearestIndex(this.track.scrollLeft);

    if (target === drag.startIndex && Math.abs(delta) > this.track.clientWidth / 5) {
      target = drag.startIndex + (delta < 0 ? 1 : -1);
    }

    target = Math.max(0, Math.min(this.items.length - 1, target));

    // Snapping comes back once the track has settled on the card, or it would
    // cut the smooth scroll short and jump. The timer covers browsers without
    // scrollend, and a target the track is already sitting on.
    this.restoreSnap = () => {
      this.cancelSnapRestore();
      this.track.style.scrollSnapType = "";
    };

    this.track.addEventListener("scrollend", this.restoreSnap, { once: true });
    this.snapTimer = setTimeout(this.restoreSnap, 600);

    this.scrollToItem(this.items[target]);
  }

  cancelSnapRestore() {
    clearTimeout(this.snapTimer);
    if (this.restoreSnap) this.track.removeEventListener("scrollend", this.restoreSnap);
    this.restoreSnap = null;
  }

  endDrag() {
    if (!this.drag) return;

    this.drag.listeners.abort();
    this.drag = null;
    this.track?.classList.remove("cross-sell__track--dragging");
  }

  /** The translated string arrives with a marker in place of the number. */
  dotLabel(position) {
    const template = this.dataset.dotLabel || "Go to item #";

    return template.replace("#", String(position));
  }

  showError(message) {
    if (!this.errorMessage) return;

    if (message) this.errorMessage.textContent = message;
    this.errorMessage.removeAttribute("hidden");
  }

  /**
   * Built here rather than in Liquid because they are pure navigation: without
   * script there is nothing for them to do and the list still scrolls.
   */
  buildDots() {
    if (this.items.length < 2) return;

    const dots = document.createElement("div");
    dots.className = "cross-sell__dots";

    this.items.forEach((item, index) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "cross-sell__dot";
      dot.dataset.index = String(index);
      dot.setAttribute("aria-label", this.dotLabel(index + 1));
      if (index === 0) dot.setAttribute("aria-current", "true");
      dots.appendChild(dot);
    });

    this.track.after(dots);
    this.dots = Array.from(dots.children);

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const index = this.items.indexOf(entry.target);

          // aria-current has to be removed rather than emptied: an empty value
          // reads as "not current", which would leave every dot unmarked.
          this.dots.forEach((dot, position) => {
            if (position === index) dot.setAttribute("aria-current", "true");
            else dot.removeAttribute("aria-current");
          });
        });
      },
      { root: this.track, threshold: 0.6 }
    );

    this.items.forEach((item) => this.observer.observe(item));
  }
}

if (!customElements.get("subscription-cross-sell")) {
  customElements.define("subscription-cross-sell", SubscriptionCrossSell);
}
